import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import FoundingProviderControls from '../components/FoundingProviderControls';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Loader2, Shield, LogOut, CheckCircle, XCircle, Calendar, MapPin, Phone, Mail, 
  Trash2, Search, Filter, AlertTriangle, Ban, PlayCircle, PauseCircle, Tag, Users
} from 'lucide-react';
import { format } from 'date-fns';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    filterProviders();
  }, [providers, searchTerm, statusFilter]);

  const loadProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProviders(response.data);
    } catch (error) {
      console.error('Error loading providers:', error);
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const filterProviders = () => {
    let filtered = [...providers];
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.businessName?.toLowerCase().includes(search) ||
        p.ownerName?.toLowerCase().includes(search) ||
        p.email?.toLowerCase().includes(search) ||
        p.city?.toLowerCase().includes(search)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.accountStatus === statusFilter);
    }
    
    setFilteredProviders(filtered);
  };

  const handleUpdateAccountStatus = async (providerId, newStatus) => {
    try {
      setActionLoading({ ...actionLoading, [providerId]: newStatus });
      
      await axios.patch(
        `${API_URL}/api/admin/providers/${providerId}/account-status`,
        { accountStatus: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Account status updated to ${newStatus}`);
      loadProviders();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading({ ...actionLoading, [providerId]: null });
    }
  };

  const handleDeleteClick = (provider) => {
    setProviderToDelete(provider);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  // Strong confirmation: typed business name must match exactly (case-insensitive).
  const deleteConfirmMatches =
    !!providerToDelete &&
    deleteConfirmText.trim().toLowerCase() ===
      (providerToDelete.businessName || '').trim().toLowerCase();

  const handleDeleteConfirm = async () => {
    if (!providerToDelete || !deleteConfirmMatches) return;

    try {
      setActionLoading({ ...actionLoading, [`delete-${providerToDelete.id}`]: true });

      await axios.delete(
        `${API_URL}/api/admin/providers/${providerToDelete.id}` +
          `?confirm_business_name=${encodeURIComponent(providerToDelete.businessName || '')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Provider "${providerToDelete.businessName}" permanently deleted`);
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
      setDeleteConfirmText('');
      loadProviders();
    } catch (error) {
      console.error('Error deleting provider:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete provider');
    } finally {
      setActionLoading({ ...actionLoading, [`delete-${providerToDelete?.id}`]: false });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusBadge = (status) => {
    const variants = {
      ACTIVE: 'bg-green-100 text-green-800 border-green-300',
      PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      DEACTIVATED: 'bg-red-100 text-red-800 border-red-300',
    };
    
    return (
      <Badge className={`${variants[status] || variants.PENDING} border`}>
        {status || 'PENDING'}
      </Badge>
    );
  };

  const getStatusCounts = () => {
    return {
      total: providers.length,
      active: providers.filter(p => p.accountStatus === 'ACTIVE').length,
      pending: providers.filter(p => p.accountStatus === 'PENDING' || !p.accountStatus).length,
      deactivated: providers.filter(p => p.accountStatus === 'DEACTIVATED').length,
    };
  };

  const counts = getStatusCounts();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-6">
              <Logo variant="compact" size="sm" />
              <div className="border-l pl-6 flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Shield className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold" data-testid="admin-title">
                    Admin Panel
                  </h1>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Admin Nav */}
      <div className="bg-card border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 py-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clients')} data-testid="nav-clients">
            <Users className="h-4 w-4 mr-1.5" /> Client Accounts
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/promo-codes')} data-testid="nav-promo-codes">
            <Tag className="h-4 w-4 mr-1.5" /> Promo Codes
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/community')} data-testid="nav-community">
            <AlertTriangle className="h-4 w-4 mr-1.5" /> Community
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/system')} data-testid="nav-system">
            <Shield className="h-4 w-4 mr-1.5" /> System
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total Providers</p>
              <p className="text-2xl font-bold">{counts.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-green-600">Active</p>
              <p className="text-2xl font-bold text-green-600">{counts.active}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-red-600">Deactivated</p>
              <p className="text-2xl font-bold text-red-600">{counts.deactivated}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
              <Filter className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="ACTIVE">Active</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mb-4">
          <p className="text-muted-foreground">
            Showing {filteredProviders.length} of {providers.length} providers
          </p>
        </div>

        {filteredProviders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {providers.length === 0 ? 'No providers registered yet' : 'No providers match your filters'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="providers-list">
            {filteredProviders.map((provider) => (
              <Card key={provider.id} data-testid={`provider-card-${provider.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <CardTitle className="text-xl">{provider.businessName}</CardTitle>
                        {getStatusBadge(provider.accountStatus)}
                        {!provider.isActive && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Visibility Off
                          </Badge>
                        )}
                      </div>
                      <CardDescription className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium">Owner:</span>
                          <span>{provider.ownerName}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium">Slug:</span>
                          <a
                            href={`/providers/${provider.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            /providers/{provider.slug}
                          </a>
                        </div>
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span>{provider.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>{provider.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{provider.city}, {provider.state}</span>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        <span>Joined: {format(new Date(provider.createdAt), 'MMM d, yyyy')}</span>
                      </div>
                      <div className="font-semibold">
                        Total Bookings: {provider.bookingCount || 0}
                      </div>
                      <div className="text-muted-foreground">
                        Service Radius: {provider.serviceRadiusMiles} miles
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t">
                    {/* Activate Button - shown when not ACTIVE */}
                    {provider.accountStatus !== 'ACTIVE' && (
                      <Button
                        size="sm"
                        onClick={() => handleUpdateAccountStatus(provider.id, 'ACTIVE')}
                        disabled={actionLoading[provider.id]}
                        data-testid={`activate-button-${provider.id}`}
                      >
                        {actionLoading[provider.id] === 'ACTIVE' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <PlayCircle className="mr-2 h-4 w-4" />
                            Activate
                          </>
                        )}
                      </Button>
                    )}
                    
                    {/* Deactivate Button - shown when ACTIVE or PENDING */}
                    {provider.accountStatus !== 'DEACTIVATED' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdateAccountStatus(provider.id, 'DEACTIVATED')}
                        disabled={actionLoading[provider.id]}
                        data-testid={`deactivate-button-${provider.id}`}
                      >
                        {actionLoading[provider.id] === 'DEACTIVATED' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <PauseCircle className="mr-2 h-4 w-4" />
                            Deactivate
                          </>
                        )}
                      </Button>
                    )}
                    
                    {/* View Public Page */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`/providers/${provider.slug}`, '_blank')}
                    >
                      View Public Page
                    </Button>
                    
                    {/* Delete Button */}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteClick(provider)}
                      disabled={actionLoading[`delete-${provider.id}`]}
                      data-testid={`delete-button-${provider.id}`}
                    >
                      {actionLoading[`delete-${provider.id}`] ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Founding Provider controls */}
                  <FoundingProviderControls
                    provider={provider}
                    onChanged={loadProviders}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog — typed confirmation required (RW-SAFE-001) */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => { setDeleteDialogOpen(o); if (!o) setDeleteConfirmText(''); }}>
        <AlertDialogContent data-testid="delete-provider-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Permanently Delete Provider
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently and irreversibly deletes <strong>{providerToDelete?.businessName}</strong> and all of its data. Consider <strong>deactivating</strong> instead — that is reversible.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="text-sm space-y-3">
            <div>
              <p className="font-medium mb-1">The following will be permanently deleted:</p>
              <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                <li>Profile, services, availability &amp; route zones</li>
                <li>All bookings, reviews &amp; emergency requests</li>
                <li>CRM clients, animals &amp; private notes</li>
                <li>Invoices, invoice settings &amp; end-of-day closeouts</li>
                <li>Consultations &amp; notifications</li>
              </ul>
            </div>
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <p className="font-semibold text-destructive">This cannot be undone.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Type <span className="font-mono bg-muted px-1 rounded">{providerToDelete?.businessName}</span> to confirm:
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={providerToDelete?.businessName || ''}
                data-testid="delete-confirm-input"
                autoComplete="off"
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={!deleteConfirmMatches}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:pointer-events-none"
              data-testid="delete-confirm-btn"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
